<template>
  <div :style="containerStyle">
    <div
      class="baseLayer-tray"
      :class="{
        opened: trayOpen && !options.showInline,
        closed: !trayOpen && !options.showInline,
        'baseLayer-tray-inline': options.showInline,
      }"
    >
      <div v-for="baseLayer in baseLayers" :key="baseLayer.id">
        <v-avatar
          :tile="false"
          :size="options.btnSize"
          :title="baseLayer.name"
          @click="selectBaseLayer(baseLayer)"
          class="baseLayer-ctrl hyp-base-layer-avatar"
        >
          <img
            :class="{ selected: baseLayer.id === userBaseLayer }"
            :src="baseLayer.thumbnail"
            alt="Change map base layer"
          />
        </v-avatar>
      </div>
    </div>

    <v-avatar
      :tile="false"
      :size="options.btnSize"
      @click="toggleTray()"
      class="hyp-base-layer-avatar"
      title="Change map base layer"
    >
      <img
        v-if="!options.showInline"
        :src="trayIcon"
        alt="Change map base layer"
      />
    </v-avatar>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import {
  DEFAULT_LAYERS_DETAILS,
  MAP_ATTRIBUTION,
} from "@/common/constants/maps";
import { loadIcons } from "@/common/maps/maps";
import { addAcetate } from "@/common/maps/features";
import { setBaseLayer } from "@/common/maps/layers";
import { ICONS } from "@/common/constants/icons";
import {
  MODULE_PREFIX_USER,
  MODULE_PREFIX_MAP,
  MODULE_PREFIX_PLACES,
} from "@/store";
import { SAVE_PREFERENCES, GET_MAP_REFRESH_DATA } from "@/store/actions.type";

const baseLayerDarkBtn = require("@/assets/images/miscellaneous/baselayer-dark-btn-60x60.png");
const baseLayerLightBtn = require("@/assets/images/miscellaneous/baselayer-light-btn-60x60.png");
const baseLayerSatelliteBtn = require("@/assets/images/miscellaneous/baselayer-satellite-btn-60x60.png");
const baseLayerStreetBtn = require("@/assets/images/miscellaneous/baselayer-street-btn-60x60.png");
const baseLayerNovaBtn = require("@/assets/images/miscellaneous/baselayer-nova-btn-60x60.png");

const mapSettingsActive = ICONS.misc.mapSettings.activeSvg;
const mapSettingsInActive = ICONS.misc.mapSettings.inactiveSvg;
const mapSettingsDarkBtn = require(`@/assets${mapSettingsActive}`);
const mapSettingsLightBtn = require(`@/assets${mapSettingsInActive}`);

export default {
  name: "BaseLayer",
  props: {
    mapId: {
      type: String,
      required: true,
    },
    options: {
      type: Object,
      default() {
        return {
          position: "absolute",
          btnSize: 36,
          bottom: 48,
          right: 145,
        };
      },
    },
  },
  mounted() {
    this.getBaseLayerPref();
  },
  data() {
    let baseLayers = [];
    baseLayers.push({
      id: DEFAULT_LAYERS_DETAILS["esri_night_time"].style,
      name: "Dark",
      thumbnail: baseLayerDarkBtn,
    });
    baseLayers.push({
      id: DEFAULT_LAYERS_DETAILS["esri_light"].style,
      name: "Light",
      thumbnail: baseLayerLightBtn,
    });
    baseLayers.push({
      id: DEFAULT_LAYERS_DETAILS["esri_world_street"].style,
      name: "Streets",
      thumbnail: baseLayerStreetBtn,
    });
    baseLayers.push({
      id: DEFAULT_LAYERS_DETAILS["custom_satellite"].style,
      name: "Satellite",
      thumbnail: baseLayerSatelliteBtn,
    });
    baseLayers.push({
      id: DEFAULT_LAYERS_DETAILS["esri_nova"].style,
      name: "Sci-fi",
      thumbnail: baseLayerNovaBtn,
    });

    return {
      trayOpen: false,
      openTrayIcon: mapSettingsDarkBtn,
      closedTrayIcon: mapSettingsLightBtn,
      trayIcon: mapSettingsLightBtn,
      defaultBaseLayer: DEFAULT_LAYERS_DETAILS["esri_world_street"].style,
      userBaseLayer: "",
      baseLayers: baseLayers,
    };
  },
  computed: {
    ...mapGetters({
      maps: MODULE_PREFIX_MAP + "maps",
      places: MODULE_PREFIX_PLACES + "places",
      userPreferences: MODULE_PREFIX_USER + "preferences",
    }),
    map() {
      return this.maps[this.mapId];
    },
    preferenceName() {
      return "baseLayer_" + this.mapId;
    },
    containerStyle() {
      return {
        position: this.options.position,
        display: "block",
        "z-index": "3",
        width: this.options.btnSize,
        bottom: this.options.bottom + "px",
        right: this.options.right + "px",
      };
    },
  },
  methods: {
    getBaseLayerPref() {
      if (!this.userPreferences.hasOwnProperty("map")) {
        this.userPreferences.map = {};
      }
      if (!this.userPreferences.map.hasOwnProperty(this.preferenceName)) {
        this.userPreferences.map[this.preferenceName] = this.defaultBaseLayer;
      }
      let baselayer;
      // if the user stills has the old mapbox baselayers switch them to the esri baselayer
      if (this.userPreferences.map[this.preferenceName].includes("mapbox://")) {
        baselayer = DEFAULT_LAYERS_DETAILS["esri_night_time"].style;
      } else if (this.userPreferences.map.hasOwnProperty(this.preferenceName)) {
        baselayer = this.userPreferences.map[this.preferenceName];
      } else {
        baselayer = this.defaultBaseLayer;
      }
      this.userBaseLayer = baselayer;
    },
    saveBaseLayerPref(baseLayer) {
      this.userBaseLayer = this.userPreferences.map[
        this.preferenceName
      ] = baseLayer;
      this.$store.dispatch(
        MODULE_PREFIX_USER + SAVE_PREFERENCES,
        this.userPreferences
      );
    },
    toggleTray: function () {
      this.trayOpen = !this.trayOpen;
      this.trayIcon = this.trayOpen ? this.openTrayIcon : this.closedTrayIcon;
    },
    selectBaseLayer: function (selectedBaseLayer) {
      //skip if already selected
      if (this.userBaseLayer === selectedBaseLayer.id) {
        return;
      }
      this.saveBaseLayerPref(selectedBaseLayer.id);
      this.map.on("style.load", this.refreshMapData);
      setBaseLayer(this.map, selectedBaseLayer.id);
      this.updateAttribution();
    },
    refreshMapData() {
      // add all the layers back to the map since setting the base layer removes all layers
      addAcetate(this.map);
      loadIcons(this.map, this.places);
      this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_REFRESH_DATA, true);
      this.map.off("style.load", this.refreshMapData);
    },
    updateAttribution() {
      const ELEM = document.querySelector(".mapboxgl-ctrl-attrib");
      if (ELEM) {
        let attribution = MAP_ATTRIBUTION.vector;
        if (
          this.userBaseLayer ===
            DEFAULT_LAYERS_DETAILS["custom_satellite"].style ||
          this.userBaseLayer ===
            DEFAULT_LAYERS_DETAILS["custom_satellite_dark"].style
        ) {
          attribution += `<br>${MAP_ATTRIBUTION.imagery}`;
        }
        ELEM.innerHTML = attribution;
      }
    },
  },
};
</script>
<style scoped>
.baseLayer-tray-inline {
  display: inline-flex;
}

.baseLayer-tray-inline div {
  padding: 0px 8px;
}

.baseLayer-tray img.selected {
  border: 4px solid var(--primary-color);
}

.baseLayer-tray.opened {
  visibility: visible;
  opacity: 1;
  transition: opacity 0.5s linear;
}

.baseLayer-tray.closed {
  visibility: hidden;
  opacity: 0;
  transition: visibility 0s 0.5s, opacity 0.5s linear;
}

.baseLayer-tray > div {
  margin-bottom: 10px;
}

.baseLayer-ctrl {
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.4);
}
.hyp-base-layer-avatar {
  cursor: pointer;
}
</style>
